/**
 * ShiftTemplatesEditor — the recurring coverage pattern on a post.
 *
 * Lives as the Shifts tab in the Job Editor, not a standalone page: a template
 * only means anything in the context of the post it covers.
 *
 * A template is retired, never deleted, once a week has been generated from it —
 * every generated shift keeps its `shift_template_id` and an orphaned pointer
 * makes a schedule impossible to explain later. Editing a template changes
 * nothing already generated; generation is a copy, not a live binding.
 */
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { schedulingAPI, jobPostsAPI, hoursTypesAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Plus, Pencil, Archive, ArchiveRestore, Moon, CalendarClock } from 'lucide-react';
import { useBeforeUnload, ConfirmDiscardDialog, guardedContentProps, useNavigationGuard } from '../common/UnsavedGuard';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAYS = [1, 2, 3, 4, 5];
const EMPTY = {
  job_post_id: '', shift_code: '', start_time: '', end_time: '', days_of_week: [],
  default_rollover_type: null, default_hours_type_id: null, primary_employee_id: null,
  backup_employee_ids: [], effective_start_date: '', effective_end_date: '', is_active: true,
};

const crossesMidnight = (a, b) => Boolean(a && b && b <= a);

function TemplateDialog({ open, onOpenChange, template, posts, hoursTypes, officers, onSave }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const baseline = useRef('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  useEffect(() => {
    const next = template
      ? { ...EMPTY, ...template, effective_start_date: template.effective_start_date || '',
          effective_end_date: template.effective_end_date || '' }
      : EMPTY;
    setForm(next);
    baseline.current = JSON.stringify(next);
    setConfirmOpen(false);
  }, [template, open]);
  const dirty = useMemo(() => open && JSON.stringify(form) !== baseline.current, [form, open]);
  useBeforeUnload(dirty);
  const requestClose = () => { if (dirty) setConfirmOpen(true); else onOpenChange(false); };
  // App-wide guard: menu clicks, in-app links and browser Back/Forward also prompt.
  useNavigationGuard('shift-template', {
    getDirty: () => dirty,
    save: () => submit(),
    discard: () => onOpenChange(false),
  });
  const change = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleDay = (d) => setForm(f => ({
    ...f,
    days_of_week: f.days_of_week.includes(d)
      ? f.days_of_week.filter(x => x !== d)
      : [...f.days_of_week, d].sort((a, b) => a - b),
  }));

  const submit = async () => {
    if (!form.job_post_id) { toast.error('Pick the post this shift covers'); return false; }
    if (!form.start_time || !form.end_time) { toast.error('A shift needs a start and an end time'); return false; }
    if (!form.days_of_week.length) { toast.error('Pick at least one day'); return false; }
    setSaving(true);
    try {
      await onSave({
        ...form,
        effective_start_date: form.effective_start_date || null,
        effective_end_date: form.effective_end_date || null,
      });
      onOpenChange(false);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the shift');
      return false;
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) requestClose(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="shift-template-dialog"
        {...guardedContentProps(requestClose)}>
        <DialogHeader>
          <DialogTitle>{template ? 'Edit shift' : 'Add a recurring shift'}</DialogTitle>
          <DialogDescription>
            This is the planned window. When a week is generated the manager gets these
            times to correct to what was actually worked — editing this pattern never
            changes a week that has already been built.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label>Post *</Label>
            <Select value={form.job_post_id} onValueChange={(v) => change('job_post_id', v)}>
              <SelectTrigger data-testid="shift-template-post">
                <SelectValue placeholder="Which post does this cover?" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {posts.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.post_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Shift code</Label>
            <Input value={form.shift_code} onChange={e => change('shift_code', e.target.value)}
              placeholder="B11" data-testid="shift-template-code" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Start *</Label>
              <Input type="time" value={form.start_time}
                onChange={e => change('start_time', e.target.value)}
                data-testid="shift-template-start" />
            </div>
            <div>
              <Label>End *</Label>
              <Input type="time" value={form.end_time}
                onChange={e => change('end_time', e.target.value)}
                data-testid="shift-template-end" />
            </div>
          </div>

          <div className="md:col-span-2">
            <Label>Days</Label>
            <div className="flex flex-wrap gap-2 mt-1.5" data-testid="shift-template-days">
              {DAYS.map((label, idx) => {
                const on = form.days_of_week.includes(idx);
                return (
                  <button key={idx} type="button" onClick={() => toggleDay(idx)}
                    data-testid={`shift-template-day-${idx}`}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                      on ? 'bg-primary text-primary-foreground border-transparent'
                         : 'bg-surface-sunken text-fg-muted border-line hover:text-fg'}`}>
                    {label}
                  </button>
                );
              })}
              <button type="button" onClick={() => change('days_of_week', [0, 1, 2, 3, 4, 5, 6])}
                className="px-3 py-1.5 rounded-full text-sm text-fg-muted hover:text-fg underline">
                Every day
              </button>
              <button type="button" onClick={() => change('days_of_week', WEEKDAYS)}
                className="px-3 py-1.5 rounded-full text-sm text-fg-muted hover:text-fg underline">
                Mon–Fri
              </button>
            </div>
            {crossesMidnight(form.start_time, form.end_time) && (
              <p className="text-xs text-fg-muted mt-2 flex items-center gap-1.5"
                data-testid="shift-template-overnight-note">
                <Moon className="h-3.5 w-3.5" />
                Crosses midnight. Each generated shift will be billed across both calendar
                days automatically — no rollover code needed.
              </p>
            )}
          </div>

          <div>
            <Label>Hours type</Label>
            <Select value={form.default_hours_type_id || '__none__'}
              onValueChange={(v) => change('default_hours_type_id', v === '__none__' ? null : v)}>
              <SelectTrigger data-testid="shift-template-hours-type">
                <SelectValue placeholder="Use the post's default" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="__none__">— Use the post's default —</SelectItem>
                {hoursTypes.map(h => (
                  <SelectItem key={h.id} value={h.id}>{h.description || h.code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Primary officer</Label>
            <Select value={form.primary_employee_id || '__none__'}
              onValueChange={(v) => change('primary_employee_id', v === '__none__' ? null : v)}>
              <SelectTrigger data-testid="shift-template-officer">
                <SelectValue placeholder="Leave open" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="__none__">— Leave the post open —</SelectItem>
                {officers.map(o => (
                  <SelectItem key={o.employee_id || o.id} value={o.employee_id || o.id}>
                    {o.display_last_first || `${o.last_name}, ${o.first_name}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>In force from</Label>
            <Input type="date" value={form.effective_start_date}
              onChange={e => change('effective_start_date', e.target.value)}
              data-testid="shift-template-eff-start" />
          </div>
          <div>
            <Label>Until</Label>
            <Input type="date" value={form.effective_end_date}
              onChange={e => change('effective_end_date', e.target.value)}
              data-testid="shift-template-eff-end" />
            <p className="text-xs text-fg-muted mt-1">
              Leave blank for a pattern with no end date.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={requestClose} data-testid="shift-template-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="shift-template-save">
            {saving ? 'Saving…' : 'Save shift'}
          </Button>
        </DialogFooter>
      </DialogContent>
      <ConfirmDiscardDialog
        open={confirmOpen}
        saving={saving}
        onSave={async () => { setConfirmOpen(false); await submit(); }}
        onDiscard={() => { setConfirmOpen(false); onOpenChange(false); }}
        onCancel={() => setConfirmOpen(false)}
      />
    </Dialog>
  );
}

export const ShiftTemplatesEditor = ({ jobId, canEdit }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRetired, setShowRetired] = useState(false);
  const [dialog, setDialog] = useState({ open: false, template: null });
  const [officers, setOfficers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [hoursTypes, setHoursTypes] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await schedulingAPI.listTemplates(jobId, { include_retired: showRetired });
      setRows(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load the shifts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [jobId, showRetired]);
  useEffect(() => {
    jobPostsAPI.list(jobId, { is_active: true })
      .then(({ data }) => setPosts(data || [])).catch(() => setPosts([]));
    hoursTypesAPI.list()
      .then(({ data }) => setHoursTypes(data || [])).catch(() => setHoursTypes([]));
    schedulingAPI.jobDetail(jobId)
      .then(({ data }) => setOfficers(data?.assignments?.length
        ? (data.assignments || []).map(a => ({ id: a.employee_id, employee_id: a.employee_id,
            display_last_first: a.employee_name || a.employee_id }))
        : []))
      .catch(() => setOfficers([]));
  }, [jobId]);

  const activePosts = useMemo(() => posts.filter(p => p.is_active !== false), [posts]);

  const save = async (payload) => {
    if (dialog.template) {
      await schedulingAPI.updateTemplate(dialog.template.id, payload);
      toast.success('Shift updated. Weeks already generated are untouched.');
    } else {
      await schedulingAPI.createTemplate(jobId, payload);
      toast.success('Shift added');
    }
    load();
  };

  const setActive = async (row, active) => {
    try {
      await schedulingAPI.setTemplateActive(row.id, active);
      toast.success(active ? 'Shift reactivated' : 'Shift retired');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not change the shift');
    }
  };

  if (!activePosts.length) {
    return (
      <div className="rounded-lg border border-line bg-surface-sunken p-8 text-center"
        data-testid="shift-templates-no-posts">
        <CalendarClock className="h-8 w-8 mx-auto text-fg-muted" />
        <p className="text-base font-medium text-fg mt-3">This job has no posts yet</p>
        <p className="text-sm text-fg-muted mt-1 max-w-md mx-auto">
          A recurring shift covers a post, so add the posts first on the Posts tab. Each
          post carries the billing item the shift will bill against.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="shift-templates-editor">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <p className="text-sm text-fg-muted max-w-2xl">
          The recurring pattern for this job. A week generated from these arrives with
          every shift, time, hours type and officer already filled in, so a manager
          reviews rather than types.
        </p>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-fg-muted">
            <Switch checked={showRetired} onCheckedChange={setShowRetired}
              data-testid="shift-templates-show-retired" />
            Show retired
          </label>
          {canEdit && (
            <Button onClick={() => setDialog({ open: true, template: null })}
              data-testid="shift-template-add">
              <Plus className="h-4 w-4 mr-1.5" /> Add shift
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-fg-muted">Loading…</p>
      ) : !rows.length ? (
        <div className="rounded-lg border border-line bg-surface-sunken p-8 text-center"
          data-testid="shift-templates-empty">
          <CalendarClock className="h-8 w-8 mx-auto text-fg-muted" />
          <p className="text-base font-medium text-fg mt-3">No recurring shifts yet</p>
          <p className="text-sm text-fg-muted mt-1 max-w-md mx-auto">
            Add one shift per post per window — a 24-hour post is three shifts, not one.
            Until then a week has to be built by hand.
          </p>
          {canEdit && (
            <Button className="mt-4" onClick={() => setDialog({ open: true, template: null })}
              data-testid="shift-template-add-first">
              <Plus className="h-4 w-4 mr-1.5" /> Add the first shift
            </Button>
          )}
        </div>
      ) : (
        <div className="rounded-lg border border-line overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Post</TableHead>
                <TableHead>Window</TableHead>
                <TableHead>Days</TableHead>
                <TableHead>Primary officer</TableHead>
                <TableHead>In force</TableHead>
                <TableHead className="text-right">Built</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.id} data-testid={`shift-template-row-${r.id}`}
                  className={r.is_active ? '' : 'opacity-60'}>
                  <TableCell className="font-medium text-fg">{r.shift_code || '—'}</TableCell>
                  <TableCell className="text-fg">{r.post_name || '—'}</TableCell>
                  <TableCell className="text-fg whitespace-nowrap">
                    {r.start_time}–{r.end_time}
                    {r.crosses_midnight && (
                      <Moon className="h-3.5 w-3.5 inline ml-1.5 text-fg-muted"
                        title="Crosses midnight" />
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {DAYS.map((d, i) => (
                        <span key={i} className={`text-xs px-1 rounded ${
                          (r.days_of_week || []).includes(i)
                            ? 'text-fg font-medium' : 'text-fg-muted/40'}`}>{d[0]}</span>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-fg">
                    {r.primary_employee_name || (
                      <span className="text-fg-muted">Open</span>
                    )}
                  </TableCell>
                  <TableCell className="text-fg-muted text-sm whitespace-nowrap">
                    {r.effective_start_date || 'Always'}
                    {r.effective_end_date ? ` → ${r.effective_end_date}` : ''}
                  </TableCell>
                  <TableCell className="text-right text-fg-muted text-sm">
                    {r.generated_shift_count || 0}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {!r.is_active && <Badge variant="outline" className="mr-2">Retired</Badge>}
                    {canEdit && (
                      <>
                        <Button variant="ghost" size="sm"
                          onClick={() => setDialog({ open: true, template: r })}
                          data-testid={`shift-template-edit-${r.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm"
                          onClick={() => setActive(r, !r.is_active)}
                          title={r.is_active
                            ? 'Retire this pattern — generated weeks keep their shifts'
                            : 'Reactivate'}
                          data-testid={`shift-template-retire-${r.id}`}>
                          {r.is_active ? <Archive className="h-4 w-4" />
                                       : <ArchiveRestore className="h-4 w-4" />}
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <TemplateDialog open={dialog.open}
        onOpenChange={(o) => setDialog(d => ({ ...d, open: o }))}
        template={dialog.template} posts={activePosts} hoursTypes={hoursTypes}
        officers={officers} onSave={save} />
    </div>
  );
};

export default ShiftTemplatesEditor;
